/**
 * Google Sign-In config — super-admin pastes Client ID + Secret for the
 * "Sign in with Google" button on /login and /signup. Stored in Mongo
 * (`app_config._id = "google_oauth"`) so production doesn't need env vars
 * or a redeploy.
 */
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LogIn, Save, CheckCircle2, XCircle, ExternalLink, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthProviderToggleRow } from "./AuthProviderToggleRow";

export const GoogleSignInConfigCard: React.FC = () => {
  const qc = useQueryClient();
  const cfg = useQuery<{ client_id: string; client_secret_set: boolean }>({
    queryKey: ["google-signin-config"],
    queryFn: async () => (await api.get("/auth/admin/google-oauth-config")).data,
  });

  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    if (cfg.data?.client_id) setClientId(cfg.data.client_id);
  }, [cfg.data?.client_id]);

  const save = useMutation({
    mutationFn: async () => {
      const patch: any = { client_id: clientId.trim() };
      if (clientSecret.trim()) patch.client_secret = clientSecret.trim();
      return (await api.put("/auth/admin/google-oauth-config", patch)).data;
    },
    onSuccess: () => {
      toast.success("Google Sign-In credentials saved · users can now log in with Google");
      setClientSecret("");
      qc.invalidateQueries({ queryKey: ["google-signin-config"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const configured = !!cfg.data?.client_id && !!cfg.data?.client_secret_set;

  const originHint = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="sg-card p-6" data-testid="google-signin-config-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center shrink-0">
          <LogIn className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold text-[color:var(--sg-fg)]">Google Sign-In</div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
            The "Continue with Google" button on <code>/login</code> and <code>/signup</code>. Keys stored in Mongo — production doesn't need env vars or a redeploy.
          </div>
        </div>
        {configured ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900" data-testid="google-signin-status">
            <CheckCircle2 className="h-3 w-3" /> Live
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200 dark:border-rose-900" data-testid="google-signin-status">
            <XCircle className="h-3 w-3" /> Not configured
          </span>
        )}
      </div>

      <AuthProviderToggleRow provider="google" label="Google Sign-In" />

      <div className="space-y-4">
        <div>
          <Label htmlFor="gsi-client-id" className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
            Client ID
          </Label>
          <Input
            id="gsi-client-id"
            data-testid="google-signin-client-id"
            placeholder="123456-abcxyz.apps.googleusercontent.com"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="mt-1 font-mono text-xs"
          />
        </div>

        <div>
          <Label htmlFor="gsi-client-secret" className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
            Client Secret {cfg.data?.client_secret_set && <span className="text-[10px] font-normal ml-1 text-emerald-600 dark:text-emerald-400">· saved (paste to replace)</span>}
          </Label>
          <div className="relative mt-1">
            <Input
              id="gsi-client-secret"
              data-testid="google-signin-client-secret"
              type={showSecret ? "text" : "password"}
              placeholder={cfg.data?.client_secret_set ? "•••••••••••••••••••••••" : "GOCSPX-…"}
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              className="font-mono text-xs pr-16"
            />
            <button
              type="button"
              onClick={() => setShowSecret((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]"
            >
              {showSecret ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        <Button
          onClick={() => save.mutate()}
          disabled={save.isPending || !clientId.trim()}
          data-testid="google-signin-save-btn"
          className="bg-[color:var(--sg-fg)] text-white hover:bg-black dark:bg-white dark:text-[color:var(--sg-fg)] dark:hover:bg-slate-200"
        >
          {save.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save credentials
        </Button>
      </div>

      <div className="mt-5 pt-5 border-t border-[color:var(--sg-border)]">
        <div className="text-xs font-semibold text-[color:var(--sg-fg)] mb-2">Google Cloud Console setup</div>
        <ol className="text-xs text-[color:var(--sg-muted)] space-y-1.5 list-decimal pl-5">
          <li>
            Open <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-[color:var(--sg-primary)] hover:underline inline-flex items-center gap-1">Google Cloud → APIs → Credentials <ExternalLink className="h-3 w-3" /></a>
          </li>
          <li>Create an <b>OAuth Client ID</b> · Application type <b>Web application</b>.</li>
          <li>
            Under <b>Authorized JavaScript origins</b>, add every domain users load the app from:
            <div className="mt-1.5 space-y-1">
              <code className="block px-2 py-1 rounded bg-[color:var(--sg-muted-bg)] text-[11px]">{originHint}</code>
              <code className="block px-2 py-1 rounded bg-[color:var(--sg-muted-bg)] text-[11px]">https://socibaba.com</code>
              <code className="block px-2 py-1 rounded bg-[color:var(--sg-muted-bg)] text-[11px]">https://www.socibaba.com</code>
            </div>
          </li>
          <li>Leave <b>Authorized redirect URIs</b> empty — SociBaba uses Google Identity Services (popup id_token flow), not server redirects.</li>
          <li>Copy the <b>Client ID</b> and <b>Client Secret</b> from Google and paste them above → <b>Save credentials</b>.</li>
          <li>Reload <code>/login</code> — the "Continue with Google" button will appear immediately (no redeploy needed).</li>
        </ol>
      </div>
    </div>
  );
};

export default GoogleSignInConfigCard;
